import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, X, Copy, Check, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * "Continue on your phone" — shows a QR that opens the TravelBio Expo app
 * pre-signed-in for the current user. Backed by `/api/auth/handoff/create`.
 */
export default function ContinueOnPhoneDialog({ onClose }) {
  const [state, setState] = useState({ token: null, deepLink: null, ttl: 60 });
  const [remaining, setRemaining] = useState(60);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  async function mint() {
    setLoading(true);
    try {
      const r = await api.post("/auth/handoff/create");
      setState({
        token: r.data.token,
        deepLink: r.data.deep_link,
        ttl: r.data.ttl_seconds || 60,
      });
      setRemaining(r.data.ttl_seconds || 60);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't mint a handoff token");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { mint(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!state.token) return;
    const id = setInterval(
      () => setRemaining((s) => (s > 0 ? s - 1 : 0)),
      1000,
    );
    return () => clearInterval(id);
  }, [state.token]);

  const expired = remaining <= 0;

  return (
    <div
      className="fixed inset-0 z-[80] bg-tb-navy/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="continue-on-phone-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-tb-cream border-4 border-tb-gold p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 text-tb-navy">
            <Smartphone className="w-5 h-5 text-tb-gold-bright" />
            <div className="font-serif italic text-2xl">Continue on your phone</div>
          </div>
          <button
            data-testid="continue-on-phone-close"
            className="text-tb-navy/70 hover:text-tb-navy p-1 tb-tap"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="font-hand italic text-tb-navy/70 text-sm mt-1">
          Open the TravelBio iOS/Android app and scan this — you'll land already
          signed in.
        </p>

        <div className="mt-5 flex items-center justify-center py-3">
          {loading || !state.deepLink ? (
            <div className="w-[240px] h-[240px] bg-white rounded-xl flex items-center justify-center border-2 border-tb-gold/60">
              <RefreshCw className="w-8 h-8 text-tb-navy/50 animate-spin" />
            </div>
          ) : (
            <div className="p-3 bg-white rounded-xl border-2 border-tb-gold/60">
              <QRCodeSVG
                value={state.deepLink}
                size={240}
                bgColor="#ffffff"
                fgColor="#1a3a5f"
                level="M"
                includeMargin={false}
              />
            </div>
          )}
        </div>

        <div className="text-center">
          <div className="font-hand text-sm text-tb-navy/75">
            {expired ? (
              <span className="text-tb-coral">This code has expired — refresh below.</span>
            ) : (
              <>
                Expires in{" "}
                <span className="font-serif italic text-lg text-tb-navy">
                  {remaining}s
                </span>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          {state.deepLink && (
            <button
              type="button"
              data-testid="continue-on-phone-copy"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(state.deepLink);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                } catch {
                  toast.error("Copy failed");
                }
              }}
              className="w-full py-2.5 rounded-xl bg-white border-2 border-tb-navy/30 font-hand text-sm text-tb-navy tb-tap flex items-center justify-center gap-2"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-emerald-600" /> copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> copy handoff link
                </>
              )}
            </button>
          )}
          {expired && (
            <button
              type="button"
              data-testid="continue-on-phone-refresh"
              onClick={mint}
              className="w-full py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright font-hand text-base border-2 border-tb-gold tb-tap flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> generate a new code
            </button>
          )}
        </div>

        <p className="mt-4 font-hand italic text-[11px] text-tb-navy/55 text-center">
          Single-use, expires in 60 seconds. Don't share this code.
        </p>
      </div>
    </div>
  );
}
